export type ServiceCategory = "Landscaping" | "Masonry";

export interface ServiceData {
  slug: string;
  title: string;
  subtitle: string;
  category: ServiceCategory;
  price: string;
  /** The single image assigned to this service — reused on the card and the detail hero. */
  image: string;
  imageAlt: string;
  /** 145–155 char meta description used by the SEO head manager. */
  metaDescription: string;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
}

const IMG = "/assets/images";

export const services: ServiceData[] = [
  {
    slug: "landscaping",
    title: "Landscaping",
    subtitle: "Complete property transformations, start to finish",
    category: "Landscaping",
    price: "Free Estimate",
    image: `${IMG}/service-landscaping.webp`,
    imageAlt: "Fully landscaped Massachusetts yard with manicured lawn, shrub borders and stone edging",
    metaDescription:
      "Full-service landscaping in Wakefield MA and the North Shore. Planting, grading, lawns, borders and complete yard transformations. Free estimates.",
    description:
      "NIMAR handles the whole yard — not just one corner of it. We plan the layout, correct the grading, build the beds, set the plantings and finish the edges so your property reads as one deliberate design instead of a collection of weekend projects. Every Massachusetts yard has its own soil, drainage and sun pattern, and we plan around what your property actually gives us.",
    features: [
      "Full property assessment, grading and drainage correction",
      "New lawn installation, seeding and sod",
      "Shrub, perennial and specimen tree planting",
      "Defined bed edging and clean transition lines",
      "Decorative stone, mulch and ground-cover work",
      "Seasonal follow-up so new plantings actually establish",
    ],
    process: [
      { step: "Walk the property", desc: "We look at grade, drainage, sun exposure and how you actually use the yard." },
      { step: "Plan and price", desc: "You get a written scope with plant list, materials and a realistic timeline." },
      { step: "Build it", desc: "Grading, hardscape, beds and planting handled by one crew in the right order." },
      { step: "Final walkthrough", desc: "We review the finished yard with you and leave care instructions for new plantings." },
    ],
  },
  {
    slug: "spring-fall-cleanups",
    title: "Spring & Fall Clean-Ups",
    subtitle: "Reset your property at both ends of the New England season",
    category: "Landscaping",
    price: "Free Estimate",
    image: `${IMG}/service-spring-fall-cleanups.webp`,
    imageAlt: "Autumn leaves cleared from a residential lawn during a fall clean-up in Massachusetts",
    metaDescription:
      "Spring and fall clean-ups in Wakefield MA. Leaf and debris removal, perennial cutbacks, bed re-edging and full haul-away. Book your seasonal reset now.",
    description:
      "New England is hard on a yard twice a year. Spring leaves behind a winter's worth of matted leaves, sand and broken limbs; fall buries everything you spent the summer maintaining. Our clean-up crews strip the property back to a clean baseline in one visit — including full debris removal, so nothing is left piled at the curb waiting on you.",
    features: [
      "Complete leaf and debris removal from lawns, beds and hardscapes",
      "Perennial cutbacks and ornamental grass trimming",
      "Winter sand, salt residue and gravel cleared off turf and walkways",
      "Storm-damaged limb and branch removal",
      "Bed re-edging and light mulch refresh available",
      "All debris hauled off-site — nothing left behind",
    ],
    process: [
      { step: "Book your window", desc: "Spring and fall slots fill fast, so we schedule you into the right week early." },
      { step: "Clear everything", desc: "Lawns, beds, walkways, driveways and fence lines are cleared in one pass." },
      { step: "Cut back and edge", desc: "Perennials trimmed, grasses cut and bed lines re-defined for a sharp finish." },
      { step: "Haul away", desc: "Every bag and pile leaves with our truck — your curb stays clear." },
    ],
  },
  {
    slug: "lawn-care-maintenance",
    title: "Weekly & Bi-Weekly Lawn Care",
    subtitle: "Consistent maintenance that keeps your lawn sharp all season",
    category: "Landscaping",
    price: "Free Estimate",
    image: `${IMG}/service-lawn-care-maintenance.webp`,
    imageAlt: "Freshly mown lawn with clean mowing stripes and trimmed shrub borders",
    metaDescription:
      "Weekly and bi-weekly lawn maintenance in Wakefield MA. Mowing, trimming, edging and blow-down on a fixed day you can count on. Free quotes available.",
    description:
      "A good lawn is the product of a schedule, not a one-off visit. NIMAR runs weekly and bi-weekly maintenance routes across Wakefield and the surrounding towns, arriving the same day each cycle so you always know when your crew is coming. Mow height is adjusted through the season — taller in July heat, tighter in spring and fall — because the same cut all year is what burns lawns out.",
    features: [
      "Weekly or bi-weekly mowing on a fixed day",
      "String trimming around beds, fences, posts and foundations",
      "Crisp mechanical edging along walkways and driveways",
      "Full blow-down of hard surfaces after every visit",
      "Seasonal mow-height adjustment to protect the turf",
      "Clippings managed — mulched or removed, your call",
    ],
    process: [
      { step: "Set the schedule", desc: "We quote your property and lock in a weekly or bi-weekly service day." },
      { step: "Mow and trim", desc: "Every visit includes mowing, trimming and edging — not just a quick cut." },
      { step: "Clean the hard surfaces", desc: "Walks, drives and patios are blown clean before the crew leaves." },
      { step: "Adjust through the season", desc: "Cut height and frequency shift with the weather to keep turf healthy." },
    ],
  },
  {
    slug: "landscape-design",
    title: "Landscape Design",
    subtitle: "A plan for the whole property before the first shovel",
    category: "Landscaping",
    price: "Free Consultation",
    image: `${IMG}/service-landscape-design.webp`,
    imageAlt: "Designed landscape layout with curved lawn, planted borders and stone walkway",
    metaDescription:
      "Custom landscape design in Wakefield MA. Full-property layouts, planting plans, drainage and phased build options. Book a free design consultation.",
    description:
      "Most yards fail because they were built one piece at a time with no plan connecting them. Landscape design fixes the order of operations: we map the whole property first — circulation, sight lines, drainage, planting zones and hardscape footprints — then price it so you can build it all at once or in phases without wasting work you already paid for.",
    features: [
      "Full-property layout with hardscape and planting zones",
      "Plant selections suited to New England winters and your light conditions",
      "Drainage and grading worked into the design, not patched later",
      "Phased build plans so you can spread the investment over seasons",
      "Material and finish selection guidance",
      "Design that carries straight through to our own build crews",
    ],
    process: [
      { step: "Consultation", desc: "We walk the property and talk through how you want to use the space." },
      { step: "Concept", desc: "You get a layout showing beds, hardscape, plantings and circulation." },
      { step: "Refine and price", desc: "We adjust the plan with you, then price it in full or in phases." },
      { step: "Build", desc: "The same team that designed it installs it — nothing gets lost in translation." },
    ],
  },
  {
    slug: "landscape-lighting",
    title: "Landscape Lighting Design",
    subtitle: "Low-voltage lighting that makes the property work after dark",
    category: "Landscaping",
    price: "Free Estimate",
    image: `${IMG}/service-landscape-lighting.webp`,
    imageAlt: "Home at dusk with warm low-voltage landscape lighting along the walkway and plantings",
    metaDescription:
      "Landscape lighting design and installation in Wakefield MA. Low-voltage path, uplight and facade lighting with buried runs. Free on-site estimates.",
    description:
      "Lighting is the cheapest way to double the hours you get out of a landscape — and the fastest way to make a house look finished from the street. We design low-voltage systems around what is actually worth lighting: the walkway you use at night, the specimen tree, the stone wall, the front elevation. Runs are buried properly and fixtures are placed to light the space, not blind the people in it.",
    features: [
      "Path and step lighting for safe night-time access",
      "Uplighting on trees, stone walls and architectural features",
      "Facade and entry lighting to lift curb appeal",
      "Patio, pergola and outdoor-living-area lighting",
      "Low-voltage transformers with timers and photocells",
      "Buried, weather-rated runs built for New England winters",
    ],
    process: [
      { step: "Night walk", desc: "We look at the property after dark to see what actually needs light." },
      { step: "Design the layout", desc: "Fixture types and placement are mapped to avoid glare and hot spots." },
      { step: "Install", desc: "Runs are buried, connections sealed and the transformer set on a timer." },
      { step: "Aim and adjust", desc: "We return after dark to fine-tune every fixture angle." },
    ],
  },
  {
    slug: "fertilization",
    title: "Fertilization & Weed Control",
    subtitle: "Feed the lawn, crowd out the weeds",
    category: "Landscaping",
    price: "Free Estimate",
    image: `${IMG}/service-fertilization.webp`,
    imageAlt: "Granular lawn fertilizer being applied to a healthy green Massachusetts lawn",
    metaDescription:
      "Lawn fertilization and weed control in Wakefield MA. Season-long feeding, crabgrass pre-emergent and broadleaf weed treatment. Free lawn assessment.",
    description:
      "A thick lawn is the best weed control there is — weeds move into thin turf, not dense turf. Our program feeds the lawn on a New England schedule: pre-emergent before crabgrass germinates in spring, balanced feeding through the growing season, and a heavy fall application that builds the root system that carries the lawn through winter.",
    features: [
      "Spring pre-emergent to stop crabgrass before it starts",
      "Balanced slow-release feeding through the growing season",
      "Targeted broadleaf weed treatment for dandelion, clover and plantain",
      "Heavy fall feeding to build winter root reserves",
      "Soil condition assessment and lime application where pH calls for it",
      "Grub and surface-insect treatment available",
    ],
    process: [
      { step: "Assess the lawn", desc: "We check turf density, weed pressure and soil condition before quoting." },
      { step: "Build the program", desc: "Applications are scheduled around the New England growing calendar." },
      { step: "Apply and monitor", desc: "Each visit is logged so you can see what went down and when." },
      { step: "Adjust", desc: "Rates and products shift based on how the lawn responds through the season." },
    ],
  },
  {
    slug: "irrigation",
    title: "Irrigation Systems",
    subtitle: "Install, repair, tune and winterize",
    category: "Landscaping",
    price: "Free Estimate",
    image: `${IMG}/service-irrigation.webp`,
    imageAlt: "Irrigation sprinkler heads watering a manicured lawn beside a hedge row",
    metaDescription:
      "Irrigation installation and repair in Wakefield MA. New sprinkler systems, zone tuning, head replacement and fall winterization. Free on-site estimates.",
    description:
      "An irrigation system either saves your lawn or quietly wastes water on the driveway — the difference is head placement and zone tuning. We install new systems zoned to how your property actually dries out, and we repair the ones that were installed wrong or beaten up by frost. Winterization is not optional in Massachusetts, and we handle that too.",
    features: [
      "New system design and installation with proper zoning",
      "Head replacement, realignment and coverage correction",
      "Leak, valve and solenoid diagnosis and repair",
      "Controller programming and seasonal schedule adjustment",
      "Drip lines for beds, borders and container plantings",
      "Fall blow-out winterization and spring start-up service",
    ],
    process: [
      { step: "Test the system", desc: "We run every zone and map coverage gaps, leaks and misdirected heads." },
      { step: "Quote the work", desc: "You get a clear list of what needs replacing versus what just needs tuning." },
      { step: "Install or repair", desc: "Work is done cleanly with minimal disruption to established turf." },
      { step: "Program and hand off", desc: "The controller is set for the season and we show you how to run it." },
    ],
  },
  {
    slug: "garden-beds-mulch",
    title: "Garden Beds & Mulch",
    subtitle: "Sharp edges, healthy soil, clean colour",
    category: "Landscaping",
    price: "Free Estimate",
    image: `${IMG}/service-garden-beds-mulch.webp`,
    imageAlt: "Freshly mulched garden bed with layered shrubs and a crisp cut edge",
    metaDescription:
      "Garden bed installation and mulching in Wakefield MA. New beds, hand-cut edging, weed removal and premium bark mulch or decorative stone. Free quotes.",
    description:
      "Nothing lifts a property faster than fresh beds. We cut a clean spade edge, pull the weeds out at the root rather than burying them, amend the soil where plantings are struggling and lay mulch at the depth that actually suppresses weeds — deep enough to work, not so deep it smothers the crowns of your shrubs.",
    features: [
      "New bed layout, excavation and soil preparation",
      "Hand-cut spade edging for a defined, long-lasting line",
      "Full weed removal at the root before mulching",
      "Premium bark mulch in black, brown or natural",
      "Decorative stone and river rock as a low-maintenance alternative",
      "Shrub and perennial planting into new beds",
    ],
    process: [
      { step: "Define the lines", desc: "We lay out bed shapes that fit the house and the way you move around it." },
      { step: "Clear and prep", desc: "Weeds pulled at the root, soil amended, edges cut clean by hand." },
      { step: "Plant", desc: "Any new shrubs or perennials go in before the mulch layer." },
      { step: "Mulch and finish", desc: "Mulch laid at proper depth, kept off trunks, hard surfaces blown clean." },
    ],
  },
  {
    slug: "masonry",
    title: "Masonry",
    subtitle: "Stone and brick work built to outlast the weather",
    category: "Masonry",
    price: "Free Estimate",
    image: `${IMG}/service-masonry.webp`,
    imageAlt: "Coursed natural stone masonry wall with tight, well-struck mortar joints",
    metaDescription:
      "Masonry contractor in Wakefield MA. Stone and brick walls, steps, piers, veneer and repairs built for New England freeze-thaw. Free on-site estimates.",
    description:
      "Masonry is the part of your property that should still look right in thirty years. That comes down to what happens below the surface: proper footings below frost depth, the right mortar mix for the stone, and joints struck so water sheds instead of sitting. NIMAR builds new stone and brick work and repairs older work that was never detailed for New England freeze-thaw.",
    features: [
      "Natural stone and brick wall construction",
      "Footings poured below frost depth so nothing heaves",
      "Piers, columns, caps and pillar work",
      "Mortar matched to the existing stone on repair work",
      "Stone steps, landings and thresholds",
      "Repair and rebuild of failing older masonry",
    ],
    process: [
      { step: "Assess and measure", desc: "We check existing conditions, drainage and what the ground under it is doing." },
      { step: "Footings first", desc: "Base work goes below frost depth — the part nobody sees is the part that lasts." },
      { step: "Lay the stone", desc: "Coursing, joints and caps set for both appearance and water shedding." },
      { step: "Clean and cure", desc: "Joints struck, faces cleaned down and the work protected while it cures." },
    ],
  },
  {
    slug: "hardscaping",
    title: "Hardscaping",
    subtitle: "Patios, walls and outdoor living space that hold up",
    category: "Masonry",
    price: "Free Estimate",
    image: `${IMG}/service-hardscaping.webp`,
    imageAlt: "Herringbone paver pathway bordered by ornamental grasses and planted beds",
    metaDescription:
      "Hardscaping in Wakefield MA. Patios, seating walls, steps and outdoor living areas built on excavated, compacted and well-drained base. Free estimates.",
    description:
      "Hardscaping is where a yard becomes usable space. Patios, seating walls, steps, fire features and the connections between them — designed together so the whole back of the property works as one room. The finished surface is the easy part; the reason our work stays flat is the excavation, base depth and compaction underneath it.",
    features: [
      "Patios, terraces and outdoor living areas",
      "Seating walls, planter walls and pillar accents",
      "Steps, landings and grade transitions",
      "Fire pit and outdoor fireplace integration",
      "Excavated, compacted base built for New England frost",
      "Drainage worked in so water leaves the space, not sits in it",
    ],
    process: [
      { step: "Design the space", desc: "Layout, size and circulation planned around how you will actually use it." },
      { step: "Excavate and base", desc: "Full excavation, graded base, compacted in lifts — the step most crews rush." },
      { step: "Build the surface", desc: "Pavers or stone set to line and grade with proper edge restraint." },
      { step: "Finish and seal", desc: "Joints filled and locked, edges cleaned, surrounding turf restored." },
    ],
  },
  {
    slug: "paver-patios",
    title: "Paver Patios",
    subtitle: "Level, well-drained patios that stay that way",
    category: "Masonry",
    price: "Free Estimate",
    image: `${IMG}/service-paver-patios.webp`,
    imageAlt: "Finished paver patio with curved border course and adjacent landscape beds",
    metaDescription:
      "Paver patio installation in Wakefield MA. Custom layouts, fully compacted base, drainage pitch, locked polymeric joints and clean borders. Free estimates.",
    description:
      "A paver patio is only as good as what's under it. We excavate to full depth, build and compact the base in lifts, screed the bedding layer flat and set pavers to a deliberate drainage pitch away from the house. Borders are locked with edge restraint and joints are filled with polymeric sand so the field stays tight instead of spreading and weeding up.",
    features: [
      "Custom layouts, patterns and border course detailing",
      "Full excavation with base compacted in lifts",
      "Drainage pitch set away from the foundation",
      "Edge restraint on every perimeter to stop spreading",
      "Polymeric jointing sand to lock joints and block weeds",
      "Seating walls, steps and fire features integrated on request",
    ],
    process: [
      { step: "Layout and pattern", desc: "We mark the footprint on site so you can see the real size before we dig." },
      { step: "Excavate and compact", desc: "Base excavated to depth, then built up and compacted in controlled lifts." },
      { step: "Set the pavers", desc: "Screeded bedding layer, pavers set to line, cuts made clean at the borders." },
      { step: "Lock and clean", desc: "Edge restraint installed, polymeric sand swept and set, site cleaned down." },
    ],
  },
  {
    slug: "paver-walkways",
    title: "Paver Walkways",
    subtitle: "Front walks and garden paths with a clean, permanent line",
    category: "Masonry",
    price: "Free Estimate",
    image: `${IMG}/service-paver-walkways.webp`,
    imageAlt: "Cobblestone paver walkway running between trimmed hedges toward a home entrance",
    metaDescription:
      "Paver walkway installation in Wakefield MA. Front walks, garden paths and steps set on a compacted base with locked, weed-free joints. Free estimates.",
    description:
      "The front walk is the first piece of your property anyone touches, and a settled, weedy walk undercuts everything behind it. We rebuild walks with proper base depth and a consistent width so the line reads straight from the street, handle grade changes with real steps rather than awkward slopes, and finish with locked joints that stay clean.",
    features: [
      "Front entry walks and side-yard connector paths",
      "Garden and stepping-stone paths through planted areas",
      "Consistent width and line so the walk reads straight",
      "Step and landing construction on sloped approaches",
      "Compacted base built to stop settling and frost heave",
      "Locked polymeric joints for a weed-free finish",
    ],
    process: [
      { step: "Set the line", desc: "We stake the route and width so the approach to the door makes sense." },
      { step: "Excavate the base", desc: "Full-depth excavation and compaction — the same standard as a patio." },
      { step: "Lay the walk", desc: "Pavers set to line and grade with clean, tight cuts on every curve." },
      { step: "Finish the edges", desc: "Edge restraint, joint sand, and turf or beds restored right up to the stone." },
    ],
  },
  {
    slug: "paver-driveways",
    title: "Paver Driveways",
    subtitle: "Driveways engineered for vehicle loads and plow blades",
    category: "Masonry",
    price: "Free Estimate",
    image: `${IMG}/service-paver-driveways.webp`,
    imageAlt: "Charcoal paver driveway leading to a modern home entrance",
    metaDescription:
      "Paver driveway installation in Wakefield MA. Heavy-duty base depth, interlocking patterns and plow-friendly flush finishes. Free on-site estimates.",
    description:
      "A driveway carries loads a patio never will, so it gets a deeper base, a heavier paver and an interlocking pattern that spreads weight instead of concentrating it. We also build for winter here: surfaces are set flush and even so a plow blade rides over the field rather than catching an edge, and drainage is pitched so meltwater leaves the apron instead of refreezing on it.",
    features: [
      "Heavy-duty base depth engineered for vehicle loads",
      "Interlocking herringbone and running-bond patterns",
      "Reinforced apron and street-transition detailing",
      "Flush, even surface that will not catch a plow blade",
      "Drainage pitch designed to move meltwater off the surface",
      "Optional contrasting border and banding detail",
    ],
    process: [
      { step: "Assess loads and grade", desc: "We look at vehicle use, slope and how water currently leaves the drive." },
      { step: "Deep excavation", desc: "Driveway-spec base depth, compacted in lifts with geotextile where needed." },
      { step: "Install the field", desc: "Interlocking pattern laid tight with reinforced edges and clean cuts." },
      { step: "Compact and lock", desc: "Final plate compaction, joint sand set, and the apron finished to the street." },
    ],
  },
  {
    slug: "retaining-walls",
    title: "Retaining Walls",
    subtitle: "Engineered walls that hold grade and shed water",
    category: "Masonry",
    price: "Free Estimate",
    image: `${IMG}/service-retaining-walls.webp`,
    imageAlt: "Dry-stack natural stone retaining wall holding a raised lawn area",
    metaDescription:
      "Retaining wall construction in Wakefield MA. Natural stone and block walls with a compacted base course, drainage stone and a proper drain outlet.",
    description:
      "Retaining walls fail for one reason: water. Soil behind a wall gets heavy when it's saturated, and a wall built without drainage behind it is only waiting for a wet spring. Every wall we build gets a compacted base course, clean drainage stone behind the face, filter fabric to keep the fines out and a drain line to take water somewhere useful.",
    features: [
      "Natural stone, segmental block and boulder wall construction",
      "Compacted, level base course below grade",
      "Clean drainage stone and filter fabric behind every wall face",
      "Perforated drain line with a proper outlet",
      "Geogrid reinforcement on taller walls",
      "Cap stones, steps and integrated planter details",
    ],
    process: [
      { step: "Read the slope", desc: "We check the grade, soil and where water is currently running before designing." },
      { step: "Base course", desc: "Excavated, compacted and levelled — every course above depends on this one." },
      { step: "Build and drain", desc: "Wall built up with drainage stone, fabric and drain line placed as we go." },
      { step: "Backfill and cap", desc: "Compacted backfill, cap stones set, and the grade above restored." },
    ],
  },
  {
    slug: "repointing-tuckpointing",
    title: "Repointing & Tuckpointing",
    subtitle: "Replace failing mortar before the masonry itself goes",
    category: "Masonry",
    price: "Free Estimate",
    image: `${IMG}/service-repointing-tuckpointing.webp`,
    imageAlt: "Exposed red brick wall showing mortar joints in need of repointing",
    metaDescription:
      "Brick repointing and tuckpointing in Wakefield MA. Failed joints ground out and repacked with mortar matched to the original. Free masonry inspections.",
    description:
      "Mortar is designed to be the sacrificial layer — it fails first so the brick or stone doesn't. Once joints recede, water gets behind the face and freeze-thaw starts breaking the units themselves, which is a far more expensive repair. We grind out failed joints to proper depth, repack with mortar matched to the original in strength and colour, and strike the profile to match the surrounding wall.",
    features: [
      "Joints ground out to correct depth, not just skim-coated",
      "Mortar matched to the original in strength, colour and texture",
      "Joint profile struck to match the existing wall",
      "Chimney, foundation, wall and step joint repair",
      "Spot repointing or full-elevation work",
      "Soft historic mortar matched on older Massachusetts homes",
    ],
    process: [
      { step: "Inspect the joints", desc: "We map which joints are failing and how deep the deterioration runs." },
      { step: "Grind out", desc: "Failed mortar cut back to sound material at the proper depth." },
      { step: "Repack", desc: "Matched mortar packed in layers and tooled to the existing joint profile." },
      { step: "Clean the face", desc: "Brick or stone faces washed down so no mortar haze is left behind." },
    ],
  },
  {
    slug: "foundation-repair",
    title: "Foundation Repair",
    subtitle: "Interior and exterior foundation crack and parge repair",
    category: "Masonry",
    price: "Free Estimate",
    image: `${IMG}/service-foundation-repair.webp`,
    imageAlt: "Foundation wall being repaired and parged along the base of a house",
    metaDescription:
      "Foundation repair in Wakefield MA. Interior and exterior crack repair, parging and grade correction that stops water reaching the wall. Free quotes.",
    description:
      "Foundation cracks are common in older Massachusetts homes, and most of them are repairable long before anything structural is at stake — but water coming through one will keep working on it. We repair from inside and outside: cracks cut and filled, block and poured walls parged back to a sound surface, and the exterior grade corrected so water stops arriving at the wall in the first place.",
    features: [
      "Interior foundation crack repair and sealing",
      "Exterior excavation, crack repair and waterproof parging",
      "Block and poured concrete foundation resurfacing",
      "Water-entry diagnosis at the source, not just the symptom",
      "Exterior grade correction to move water away from the wall",
      "Honest assessment when a structural engineer should be involved",
    ],
    process: [
      { step: "Diagnose", desc: "We trace where water is actually entering rather than patching the wet spot." },
      { step: "Prepare the surface", desc: "Cracks cut out and loose material removed back to sound substrate." },
      { step: "Repair and seal", desc: "Cracks filled and the wall parged or coated depending on the condition." },
      { step: "Fix the outside", desc: "Grade, downspouts and drainage corrected so the repair stays dry." },
    ],
  },
  {
    slug: "chimney-repair",
    title: "Chimney & Flashing Repair",
    subtitle: "Crowns, caps, joints and the flashing that keeps water out",
    category: "Masonry",
    price: "Free Estimate",
    image: `${IMG}/service-chimney-repair.webp`,
    imageAlt: "Brick chimney with a masonry cap on a tiled residential roof",
    metaDescription:
      "Chimney and flashing repair in Wakefield MA. Crown rebuilds, stack repointing, rain caps and flashing replacement that stops leaks. Free inspections.",
    description:
      "A chimney takes weather from every direction and is usually the last thing anyone inspects. Cracked crowns, receding joints and old flashing are behind a large share of the ceiling stains we get called about. We rebuild crowns, repoint the stack, set caps to keep rain and animals out, and replace flashing properly at the roof line instead of burying the problem in tar.",
    features: [
      "Chimney crown repair and full rebuild",
      "Stack repointing above and below the roof line",
      "Rain cap and spark arrestor installation",
      "Step and counter flashing replacement, correctly lapped",
      "Full or partial rebuild of deteriorated stacks",
      "Water-repellent treatment on exposed masonry",
    ],
    process: [
      { step: "Inspect the stack", desc: "Crown, joints, cap and flashing checked from the roof, not from the ground." },
      { step: "Report honestly", desc: "You get photos and a clear scope — repair where possible, rebuild only where needed." },
      { step: "Do the work", desc: "Crown, pointing, cap and flashing addressed together so nothing is left open." },
      { step: "Verify", desc: "We check the finished work for water paths before we call it done." },
    ],
  },
  {
    slug: "stone-veneer",
    title: "Stone Veneer",
    subtitle: "Real stone facing on foundations, facades and columns",
    category: "Masonry",
    price: "Free Estimate",
    image: `${IMG}/service-stone-veneer.webp`,
    imageAlt: "Dressed stone veneer facing installed on a residential exterior wall",
    metaDescription:
      "Stone veneer installation in Wakefield MA. Foundation facing, facade accents, columns and chimney veneer with correct moisture detailing behind it.",
    description:
      "Stone veneer is the fastest way to change how a house reads from the street — an exposed concrete foundation, a plain entry column or a flat facade turns into something with weight and texture. Done right it also lasts, which means correct moisture barrier, lath and scratch coat behind the stone, plus flashing and weep detailing so water that gets behind the face has a way out.",
    features: [
      "Exposed foundation and knee-wall facing",
      "Facade accents, wainscot bands and gable details",
      "Entry columns, pillars and mailbox surrounds",
      "Chimney and fireplace veneer, interior and exterior",
      "Moisture barrier, lath and scratch coat installed correctly",
      "Flashing and weep detailing so trapped water can escape",
    ],
    process: [
      { step: "Select the stone", desc: "We match colour, size and texture to the house and surrounding masonry." },
      { step: "Prepare the wall", desc: "Moisture barrier, lath and scratch coat go on before any stone is set." },
      { step: "Set the stone", desc: "Stone laid for natural variation with tight, consistent joints." },
      { step: "Point and clean", desc: "Joints pointed, faces cleaned and weeps checked clear." },
    ],
  },
  {
    slug: "fire-pits-fireplaces",
    title: "Fire Pits & Outdoor Fireplaces",
    subtitle: "The feature that makes the patio worth using in October",
    category: "Masonry",
    price: "Free Estimate",
    image: `${IMG}/service-fire-pits-fireplaces.webp`,
    imageAlt: "Outdoor fire pit with seating under a pergola at dusk",
    metaDescription:
      "Fire pit and outdoor fireplace construction in Wakefield MA. Wood and gas builds with fire-rated cores, safe clearances and matched face stone. Free quotes.",
    description:
      "In New England a fire feature is what buys you the shoulder seasons — it turns a patio you use in July into one you use from April to November. We build wood-burning and gas fire pits and full outdoor fireplaces with fire-rated cores, correct clearances from combustibles and a face that matches the surrounding hardscape rather than looking bolted on.",
    features: [
      "Wood-burning and gas fire pit construction",
      "Full outdoor fireplaces with chimney and hearth",
      "Fire-rated firebrick core with proper clearances",
      "Face stone matched to your existing patio or walls",
      "Integrated seating walls and wood storage niches",
      "Gas line coordination with your licensed plumber",
    ],
    process: [
      { step: "Site and clearances", desc: "We place the feature for wind, seating and safe distance from structures." },
      { step: "Footing and core", desc: "Footing poured and the fire-rated core built before any face stone goes on." },
      { step: "Face and cap", desc: "Veneer and cap set to tie into the surrounding hardscape." },
      { step: "First burn", desc: "We walk you through safe operation and cure the unit before handover." },
    ],
  },
  {
    slug: "stair-repair",
    title: "Stair & Step Repair",
    subtitle: "Safe, level entry steps that stop moving",
    category: "Masonry",
    price: "Free Estimate",
    image: `${IMG}/service-stair-repair.webp`,
    imageAlt: "Natural stone entry steps leading up to a residential doorway",
    metaDescription:
      "Masonry stair and step repair in Wakefield MA. Settled steps rebuilt on proper footings with even risers and treads pitched to shed water. Free quotes.",
    description:
      "Settled front steps are both an eyesore and a genuine trip hazard, and uneven risers are the ones people actually fall on. Most settling traces back to a footing that never went below frost depth. We reset or rebuild steps on a proper base, bring every riser to a consistent height, and pitch treads so water runs off instead of freezing on the surface.",
    features: [
      "Resetting settled, tipped or separated steps",
      "Full rebuilds on footings below frost depth",
      "Consistent riser heights across the full run",
      "Tread pitch set so water sheds instead of pooling",
      "Tread, riser and landing replacement in stone or brick",
      "Railing anchor points set solidly into the masonry",
    ],
    process: [
      { step: "Assess the movement", desc: "We find out why the steps moved before deciding to reset or rebuild." },
      { step: "Rebuild the base", desc: "Footing corrected below frost depth so the repair does not repeat." },
      { step: "Set the run", desc: "Risers brought to consistent height and treads pitched to shed water." },
      { step: "Point and finish", desc: "Joints pointed, edges cleaned and railing anchors set solid." },
    ],
  },
];

export const servicesByCategory = (category: ServiceCategory) =>
  services.filter((s) => s.category === category);

export const getService = (slug: string) => services.find((s) => s.slug === slug);
